import React from 'react';
import { ChatBubbleLeftRightIcon, CircleStackIcon } from '@heroicons/react/24/outline';
import { ChatBubbleLeftRightIcon as ChatBubbleSolidIcon, CircleStackIcon as CircleStackSolidIcon } from '@heroicons/react/24/solid';
import Tooltip from '../common/Tooltip';

export type ViewMode = 'workflow' | 'chat';

interface ViewModeSwitcherProps {
  currentMode: ViewMode;
  onModeChange: (mode: ViewMode) => void;
  hasAssistantSteps: boolean;
  disabled?: boolean;
  className?: string;
}

const ViewModeSwitcher: React.FC<ViewModeSwitcherProps> = ({
  currentMode,
  onModeChange,
  hasAssistantSteps,
  disabled = false,
  className = ''
}) => {
  // Only render if there are assistant steps
  if (!hasAssistantSteps) {
    return null;
  }

  const ModeButton: React.FC<{
    mode: ViewMode;
    icon: React.ReactNode;
    activeIcon: React.ReactNode;
    label: string;
    description: string;
  }> = ({ mode, icon, activeIcon, label, description }) => {
    const isActive = currentMode === mode;

    return (
      <button
        onClick={() => onModeChange(mode)}
        disabled={disabled}
        className={`
          flex items-center gap-3 px-4 py-3 rounded-lg
          transition-all duration-200
          ${isActive
            ? 'bg-blue-500 text-white shadow-md'
            : 'bg-white dark:bg-cap-dark text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 dark:bg-gray-900 border border-gray-300 dark:border-gray-600'
          }
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
        `}
      >
        <div className="w-6 h-6">
          {isActive ? activeIcon : icon}
        </div>
        <div className="text-left">
          <div className="font-semibold">{label}</div>
          <div className={`text-xs ${isActive ? 'text-blue-100' : 'text-gray-500 dark:text-gray-400'}`}>
            {description}
          </div>
        </div>
      </button>
    );
  };

  return (
    <div className={`inline-flex gap-2 p-1 bg-gray-100 dark:bg-gray-800 rounded-lg ${className}`}>
      <ModeButton
        mode="workflow"
        icon={<CircleStackIcon className="w-6 h-6" />}
        activeIcon={<CircleStackSolidIcon className="w-6 h-6" />}
        label="Workflow View"
        description="Visual flow diagram"
      />
      <ModeButton
        mode="chat"
        icon={<ChatBubbleLeftRightIcon className="w-6 h-6" />}
        activeIcon={<ChatBubbleSolidIcon className="w-6 h-6" />}
        label="Chat View"
        description="Conversation interface"
      />
    </div>
  );
};

// Compact version for smaller spaces
export const CompactViewModeSwitcher: React.FC<ViewModeSwitcherProps> = ({
  currentMode,
  onModeChange,
  hasAssistantSteps,
  disabled = false,
  className = ''
}) => {
  if (!hasAssistantSteps) {
    return null;
  }

  const ModeTab: React.FC<{
    mode: ViewMode;
    icon: React.ReactNode;
    label: string;
  }> = ({ mode, icon, label }) => {
    const isActive = currentMode === mode;

    return (
      <Tooltip content={label}>
      <button
        onClick={() => onModeChange(mode)}
        disabled={disabled}
        className={`
          flex items-center gap-2 px-3 py-2 rounded-md
          transition-all duration-200 text-sm
          ${isActive
            ? 'bg-white dark:bg-cap-dark text-primary-600 shadow-sm'
            : 'text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:text-gray-200'
          }
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
        `}
      >
        {icon}
        <span className="hidden sm:inline">{label}</span>
      </button>
      </Tooltip>
    );
  };

  return (
    <div className={`inline-flex gap-1 p-1 bg-gray-100 dark:bg-gray-800 rounded-md ${className}`}>
      <ModeTab
        mode="workflow"
        icon={<CircleStackIcon className="w-5 h-5" />}
        label="Workflow"
      />
      <ModeTab
        mode="chat"
        icon={<ChatBubbleLeftRightIcon className="w-5 h-5" />}
        label="Chat"
      />
    </div>
  );
};

export default ViewModeSwitcher;